import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { USERS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserPlus, Edit2, Trash2, Plus, ShieldCheck, Shield, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin", label: "Master Admin", icon: ShieldCheck, hint: "Akses penuh, kelola semua proyek & pengguna" },
  { value: "project_admin", label: "Admin Anggota", icon: Shield, hint: "Admin hanya pada proyek yang ditugaskan" },
  { value: "user", label: "User", icon: UserIcon, hint: "Read-only pada proyek yang diundang" },
];

function RoleBadge({ role }) {
  const r = ROLE_OPTIONS.find((x) => x.value === role) || ROLE_OPTIONS[2];
  const cls =
    role === "admin"
      ? "bg-primary/10 text-primary border-primary/20"
      : role === "project_admin"
      ? "bg-accent/10 text-accent border-accent/20"
      : "bg-secondary text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium border ${cls}`}>
      <r.icon className="w-3 h-3" /> {r.label}
    </span>
  );
}

export default function Users() {
  const { currentId, current, fetchProjects } = useProject();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAsAdmin, setInviteAsAdmin] = useState(false);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", role: "user" });

  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "user" });

  const fetch_ = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { fetch_(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", createForm);
      toast.success("Akun dibuat");
      setCreateOpen(false);
      setCreateForm({ name: "", email: "", password: "", role: "user" });
      fetch_();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat akun");
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name, email: u.email, password: "", role: u.role });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    const body = { name: editForm.name, email: editForm.email, role: editForm.role };
    if (editForm.password.trim()) body.password = editForm.password;
    try {
      await api.patch(`/users/${editTarget.id}`, body);
      toast.success("Akun diperbarui");
      setEditTarget(null);
      fetch_();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const remove = async (u) => {
    if (u.id === currentUser.id) {
      toast.error("Tidak bisa menghapus akun sendiri");
      return;
    }
    if (!window.confirm(`Hapus akun ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Akun dihapus");
      fetch_();
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!currentId) {
      toast.error("Pilih proyek terlebih dulu");
      return;
    }
    try {
      await api.post(`/projects/${currentId}/invite`, {
        email: inviteEmail,
        project_id: currentId,
        as_admin: inviteAsAdmin,
      });
      toast.success(inviteAsAdmin ? "Ditambahkan sebagai Admin Anggota" : "Ditambahkan sebagai User");
      setInviteEmail("");
      setInviteAsAdmin(false);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengundang");
    }
  };

  return (
    <div>
      <PageHeader
        title="Manajemen Pengguna"
        overline="Master Admin"
        description="Tambah/edit/hapus akun. Tetapkan role: Master Admin, Admin Anggota (per-proyek), atau User."
        actions={
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="users-create-button">
                <Plus className="w-4 h-4 mr-1" /> Akun Baru
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Buat Akun</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitCreate} className="space-y-3">
                <div className="space-y-2">
                  <Label>Nama</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required data-testid="users-create-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required data-testid="users-create-email" />
                </div>
                <div className="space-y-2">
                  <Label>Password (min 6)</Label>
                  <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} minLength={6} required data-testid="users-create-password" />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                    <SelectTrigger data-testid="users-create-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          <div className="flex items-center gap-2">
                            <r.icon className="w-4 h-4" />
                            <span>{r.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{ROLE_OPTIONS.find((r) => r.value === createForm.role)?.hint}</p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="users-create-submit">Buat Akun</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="border border-border rounded-lg bg-card p-6 mb-6 max-w-3xl">
        <div className="overline text-muted-foreground mb-3">Undang ke proyek aktif</div>
        <p className="text-sm text-muted-foreground mb-4">
          Proyek aktif: <span className="font-medium text-foreground">{current?.name || "—"}</span>
        </p>
        <form onSubmit={invite} className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px] space-y-1">
            <Label className="text-xs">Email user</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@cinnabit.id"
              required
              data-testid={USERS.inviteEmailInput}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Sebagai</Label>
            <Select value={inviteAsAdmin ? "admin" : "user"} onValueChange={(v) => setInviteAsAdmin(v === "admin")}>
              <SelectTrigger className="w-[180px]" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User (read-only)</SelectItem>
                <SelectItem value="admin">Admin Anggota</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid={USERS.inviteButton}>
            <UserPlus className="w-4 h-4 mr-1" /> Undang
          </Button>
        </form>
        <p className="text-xs text-muted-foreground mt-3">Akun harus sudah dibuat terlebih dulu via tombol "Akun Baru".</p>
      </div>

      <div className="border border-border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Bergabung</TableHead>
              <TableHead className="w-24 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">
                  {u.name}
                  {u.id === currentUser.id && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">(Anda)</span>}
                </TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell><RoleBadge role={u.role} /></TableCell>
                <TableCell className="text-muted-foreground text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(u)} disabled={u.id === currentUser.id} data-testid={`user-delete-${u.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Akun</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div className="space-y-2">
                <Label>Nama</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="user-edit-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="user-edit-email" />
              </div>
              <div className="space-y-2">
                <Label>Password baru <span className="text-xs text-muted-foreground">(kosongkan jika tidak diubah)</span></Label>
                <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="user-edit-password" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="user-edit-submit">Simpan</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
